import React, { useEffect, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { useDispatch } from "react-redux";
import Table from "../components/Table";
import { usHttp } from "../custom-hooks/use-http";
import appvariables from "../appvariables";
import { notificationsliceactions } from "../store/notification";
import { loadingsliceactions } from "../store/loading-slice";

const UngroupedProducts = () => {
    const { products: initialProducts } = useLoaderData();
    const [products, setProducts] = useState(initialProducts || []);
    const [productGroups, setProductGroups] = useState([]);
    const masterdispatcher = useDispatch();

    useEffect(() => {
        setProducts(initialProducts || []);
    }, [initialProducts]);

    // Load all existing product groups for selection
    useEffect(() => {
        const loadGroups = async () => {
            const responsedata = await usHttp(appvariables.apiurl + 'product/pggetall/', 'POST', {});
            if (responsedata && responsedata.status === 'Success') {
                setProductGroups(responsedata.productgroups || []);
            }
        };
        loadGroups();
    }, []);

    const handleAddToGroup = async (productId, groupId, inputElement) => {
        if (!groupId) {
            return;
        }

        const product = products.find(p => p.id === productId);
        if (!product) {
            return;
        }

        masterdispatcher(loadingsliceactions.updateloading({ state: 'loading' }));

        try {
            // Get existing group details
            const pgdetailsResponse = await usHttp(
                appvariables.apiurl + 'product/productgroup/getone/',
                'POST',
                { pgid: groupId }
            );

            if (!pgdetailsResponse || pgdetailsResponse.status !== 'Success') {
                masterdispatcher(
                    notificationsliceactions.updatenotification({
                        message: pgdetailsResponse ? pgdetailsResponse.message : 'Unable to fetch Product Group details',
                        status: 'Error'
                    })
                );
                return;
            }

            const pgdetails = pgdetailsResponse.pgdetails;
            const existingProducts = pgdetails.products || [];

            // Check if product already exists in the group (by name, package, company)
            const alreadyPresent = existingProducts.some(
                p =>
                    p.name === product.Name &&
                    p.package === product.Package &&
                    p.company === product.Company
            );

            const updatedProducts = alreadyPresent
                ? existingProducts
                : [
                    ...existingProducts,
                    {
                        name: product.Name,
                        package: product.Package,
                        company: product.Company
                    }
                ];

            // Update group with new product list
            const editResponse = await usHttp(
                appvariables.apiurl + 'product/editproductgroup/',
                'POST',
                {
                    minimumquantity: pgdetails.mq,
                    products: updatedProducts,
                    pgid: groupId
                }
            );

            if (editResponse) {
                masterdispatcher(
                    notificationsliceactions.updatenotification({
                        message: editResponse.message,
                        status: editResponse.status
                    })
                );

                if (editResponse.status === 'Success' && !alreadyPresent) {
                    // Remove product from ungrouped list after successful addition
                    setProducts(prev => prev.filter(p => p.id !== productId));
                }
            }
        } finally {
            masterdispatcher(loadingsliceactions.updateloading({ state: '' }));
            if (inputElement) {
                inputElement.value = '';
            }
        }
    };

    const handleAddToGroupByName = (productId, groupName, inputElement) => {
        const trimmedName = (groupName || '').trim();
        if (!trimmedName) {
            return;
        }
        const group = productGroups.find(pg => pg.name === trimmedName);
        if (!group) {
            masterdispatcher(
                notificationsliceactions.updatenotification({
                    message: 'Select a valid Product Group from suggestions',
                    status: 'Error'
                })
            );
            return;
        }
        handleAddToGroup(productId, group.id, inputElement);
    };

    const tablefields = [
        { name: 'Id', key: 'id', cs: { width: '50px' } },
        { name: 'Name', filter: 1, key: 'Name' },
        { name: 'Package', filter: 1, key: 'Package', cs: { width: '100px' } },
        { name: 'Company', filter: 1, key: 'Company' },
        { name: 'Quantity', key: 'Quantity', cs: { width: '80px' } },
        {
            name: 'Add to Group',
            key: 'id',
            cs: { width: '260px' },
            transformer: (id) => {
                return (
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <input
                            list="product-groups-list"
                            placeholder="Type group name"
                            style={{ flex: 1 }}
                        />
                        <button
                            type="button"
                            onClick={(e) =>
                                handleAddToGroupByName(
                                    id,
                                    e.target.previousSibling.value,
                                    e.target.previousSibling
                                )
                            }
                            style={{ padding: '2px 8px', fontSize: '12px' }}
                        >
                            Add
                        </button>
                    </div>
                );
            }
        }
    ];

    return (
        <React.Fragment>
            <h2>Products Not in Any Product Group</h2>
            {productGroups.length > 0 && (
                <datalist id="product-groups-list">
                    {productGroups.map((pg) => (
                        <option key={pg.id} value={pg.name} />
                    ))}
                </datalist>
            )}
            <Table data={products} tablefields={tablefields} />
        </React.Fragment>
    );
};

export default UngroupedProducts;
